import i18n from '../../i18n';
import { ApiError } from './client';
import { showToast } from '../ui/Toast';

type AuthExpiryHandler = () => void;

let authExpiryHandler: AuthExpiryHandler | undefined;

export function setAuthExpiryHandler(handler: AuthExpiryHandler | undefined) {
  authExpiryHandler = handler;
}

const AUTH_PAGE_RE = /^\/(login|signup|verify|forgot-password)/;

function isAuthPage(): boolean {
  if (typeof window === 'undefined') return false;
  return AUTH_PAGE_RE.test(window.location.pathname);
}

/**
 * Never retry the HTTP error codes below: a retry cannot change their outcome.
 */
export function shouldRetry(status: number, failureCount: number): boolean {
  if (status >= 400 && status < 500) return false;
  if (status >= 500) return failureCount < 2;
  return failureCount < 3;
}

export function isRetryable(error: unknown, failureCount: number): boolean {
  if (error instanceof ApiError) {
    return shouldRetry(error.status, failureCount);
  }
  return failureCount < 3;
}

/**
 * The one place the app reacts to account-level failures. Runs for React Query
 * queries and mutations that the pages did not handle themselves.
 */
export function unhandledError(error: unknown, origin: 'query' | 'mutation') {
  if (!(error instanceof ApiError)) return;

  // Form pages render their own inline messages; stay out of the way.
  if (isAuthPage()) return;

  if (error.status === 401 && error.code !== 'INVALID_CREDENTIALS') {
    showToast(i18n.t('errors.sessionExpired'));
    authExpiryHandler?.();
    return;
  }

  if (error.status === 429) {
    const seconds = error.retryAfterSeconds;
    showToast(
      seconds ? i18n.t('errors.rateLimitedWait', { seconds }) : i18n.t('errors.rateLimited'),
    );
    return;
  }

  if (error.status >= 500) {
    if (origin === 'query') {
      const ref = error.requestId;
      showToast(
        ref ? `${i18n.t('errors.serverError')} · ref ${ref}` : i18n.t('errors.serverError'),
      );
    }
    return;
  }
}
